import { useEffect, useState } from 'react';
import { Check, ChevronRight, Copy, CreditCard, Landmark, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { IPaymentOptions, formatIban } from '../models/payment-options.model';

interface PaymentMethodChoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    options: IPaymentOptions;
    /** Stripe ammesso dal TA E account di chi incassa attivo. */
    stripeAvailable: boolean;
    onPayOnline: () => void;
    isPayingOnline?: boolean;
    /** Importo gia' formattato della rata (o del totale) che si sta pagando. */
    amountLabel: string;
    /** Causale da proporre se il TA non ne ha scritta una. */
    fallbackReference: string;
}

/**
 * Il "Paga" del cliente quando il TA ha abilitato il bonifico.
 *
 * Con entrambi i metodi si sceglie; con il solo bonifico si apre gia' sulle coordinate.
 * Il bonifico non crea nulla lato server: il cliente legge le coordinate, paga dalla sua
 * banca e manda la contabile; l'incasso lo registra il TA con "segna come pagata".
 */
export const PaymentMethodChoiceModal = ({
    isOpen,
    onClose,
    options,
    stripeAvailable,
    onPayOnline,
    isPayingOnline = false,
    amountLabel,
    fallbackReference,
}: PaymentMethodChoiceModalProps) => {
    const { t } = useI18n();
    const [view, setView] = useState<'choose' | 'bank'>(stripeAvailable ? 'choose' : 'bank');
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) setView(stripeAvailable ? 'choose' : 'bank');
    }, [isOpen, stripeAvailable]);

    const copy = async (key: string, value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(key);
            setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
        } catch {
            // Clipboard negata (iframe, http): il valore resta comunque leggibile e selezionabile.
        }
    };

    const reference = options.bank_reference?.trim() || fallbackReference;

    const rows: { key: string; label: string; value: string | null | undefined; copyValue?: string; mono?: boolean }[] = [
        { key: 'amount', label: t('paymentOptions.client.amount'), value: amountLabel },
        { key: 'holder', label: t('paymentOptions.bank.accountHolder'), value: options.bank_account_holder },
        {
            key: 'iban',
            label: t('paymentOptions.bank.iban'),
            value: formatIban(options.bank_iban),
            copyValue: (options.bank_iban ?? '').replace(/\s+/g, ''),
            mono: true,
        },
        { key: 'bic', label: t('paymentOptions.bank.bic'), value: options.bank_bic, mono: true },
        { key: 'bank', label: t('paymentOptions.bank.bankName'), value: options.bank_name },
        { key: 'reference', label: t('paymentOptions.client.reference'), value: reference },
    ];

    const methodButton = (
        Icon: typeof CreditCard,
        title: string,
        description: string,
        onClick: () => void,
        busy = false,
    ) => (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            className="w-full flex items-center gap-3 rounded-lg border border-gray-200 p-4 text-left hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
            <Icon className="h-5 w-5 text-gray-700 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{title}</div>
                <div className="text-xs text-gray-500">{description}</div>
            </div>
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={view === 'choose' ? t('paymentOptions.client.chooseTitle') : t('paymentOptions.client.bankTitle')}
            size="sm"
            footer={
                <div className="flex flex-1 items-center justify-between gap-2">
                    {view === 'bank' && stripeAvailable ? (
                        <Button variant="ghost" size="sm" onClick={() => setView('choose')}>
                            {t('common.back')}
                        </Button>
                    ) : <span />}
                    <Button variant="outline" size="sm" onClick={onClose}>
                        {t('paymentOptions.client.close')}
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-3 px-6 py-4">
                {view === 'choose' ? (
                    <>
                        {methodButton(
                            CreditCard,
                            t('paymentOptions.client.payOnline'),
                            t('paymentOptions.client.payOnlineDesc'),
                            onPayOnline,
                            isPayingOnline,
                        )}
                        {methodButton(
                            Landmark,
                            t('paymentOptions.client.payBank'),
                            t('paymentOptions.client.payBankDesc'),
                            () => setView('bank'),
                        )}
                    </>
                ) : (
                    <>
                        <dl className="rounded-lg bg-gray-50 divide-y divide-gray-100">
                            {rows
                                .filter((r) => !!r.value?.trim())
                                .map((r) => (
                                    <div key={r.key} className="flex items-center justify-between gap-3 px-3 py-2">
                                        <div className="min-w-0">
                                            <dt className="text-xs text-gray-500">{r.label}</dt>
                                            <dd className={`text-sm text-gray-900 break-all ${r.mono ? 'font-mono' : 'font-medium'}`}>
                                                {r.value}
                                            </dd>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 shrink-0"
                                            onClick={() => copy(r.key, r.copyValue ?? r.value!)}
                                            aria-label={t('paymentOptions.client.copy')}
                                        >
                                            {copied === r.key ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                ))}
                        </dl>

                        {options.bank_instructions?.trim() && (
                            <p className="text-sm text-gray-700 whitespace-pre-line">{options.bank_instructions}</p>
                        )}

                        <p className="text-sm text-gray-700 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 whitespace-pre-line">
                            {options.bank_receipt_note?.trim() || t('paymentOptions.bank.defaultReceiptNote')}
                        </p>
                    </>
                )}
            </div>
        </Modal>
    );
};
